import AbstractFormulaNode from "./AbstractFormulaNode.js";
import NaFormulaNode from "./NaFormulaNode.js";

/**
 * `SumFormulaNode` represents an addition. The difference with
 * {@link AdditionFormulaNode} is that the latter is a binary operation
 * whether the former can have as many operands as is required.
 *
 * `AbstractBinaryOperatorFormulaNode` is not allowed inside a `SumFormulaNode`
 * instead the `SumFormulaNode` is added as left operand and integrated on the
 * binary tree.
 *
 * @param {AbstractFormulaNode[]} operands - child nodes. This nodes can be
 * `ReportReferenceFormulaNode` or `NaFormulaNode` depending on the last operation
 * over the collection. The last operand is a `NaFormulaNode` when an operand is removed
 * from FormulaEditorWidget. This is needed to keep the same behavior on the UI.
 */
export default class SumFormulaNode extends AbstractFormulaNode {
	constructor({ operands = [] } = {}) {
		super(...arguments);

		this._operands = operands;
	}

	getOperands() {
		return [...this._operands];
	}

	forEach(fn) {
		super.forEach(fn);

		this._operands.forEach((operand) => operand.forEach(fn));
	}

	accept(visitor, additionalInformation) {
		return visitor.visitSumNode(this, additionalInformation);
	}

	expandFormula(report) {
		const expandedOperands = this._operands.map((operand) =>
			operand.expandFormula(report),
		);

		return new this.constructor({ operands: expandedOperands });
	}

	hasReferenceTo(item) {
		return this._operands.some((operand) => operand.hasReferenceTo(item));
	}

	validate() {
		this._operands.forEach((operand) => operand.validate(...arguments));
	}

	add(node) {
		return node.addToSum(this);
	}

	addToBinaryOperator(node) {
		return new node.constructor({
			leftOperand: this,
			rightOperand: new NaFormulaNode(),
		});
	}

	removeLastNode() {
		if (this._operands.length === 1) {
			return this._operands.at(0);
		}

		const lastNode = this._operands.at(-1);
		return lastNode.removeLastNodeFromSum(this);
	}

	removeLastNodeFrom(node) {
		return new node.constructor({
			leftOperand: node.getLeftOperand(),
			rightOperand: this.removeLastNode(),
		});
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.operands = this._operands.map((operand) =>
			operand.serialize(),
		);

		return properties;
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		data.operands = context.factory.materializeAll(data.operands, context);
	}

	static getType() {
		return "SumNode";
	}
}
